import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ClipboardList,
  Image as ImageIcon,
  Minus,
  Plus,
  X,
} from 'lucide-react';
import AppShell from '../app/AppShell';
import {
  FeedbackMessage,
  FilterField,
  ModalBackdrop,
  FilterPanel,
  PaginationFooter,
  TableEmptyOverlay,
  ToastStack,
} from '../components/common';
import useReceiving from '../hooks/useReceiving';
import { buildAssetUrl } from '../services/apiConfig';
import { formatShortDate } from '../utils/formatters';

const initialFilterValues = {
  search: '',
  date: '',
  warehouseLocation: 'All',
};
const INVENTORY_PAGE_SIZE = 10;
const INVENTORY_SIMPLE_PAGE_SIZE = 20;
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const INVENTORY_VIEW_MODES = {
  detailed: 'detailed',
  simple: 'simple',
};

function InventoryPage({ navigationItems, activePath, onNavigate }) {
  const { inventoryItems, isLoading, loadError } = useReceiving({ inventoryOnly: true });
  const groupedInventoryItems = useMemo(
    () => groupSamePurchaseOrderInventoryItems(inventoryItems),
    [inventoryItems]
  );
  const releasableInventoryItems = useMemo(
    () =>
      groupedInventoryItems.filter(
        (item) =>
          item.isInventoryReady &&
          item.category !== 'Reject' &&
          getAvailableKgNet(item) > 0
      ),
    [groupedInventoryItems]
  );
  const [filterValues, setFilterValues] = useState(initialFilterValues);
  const [appliedFilters, setAppliedFilters] = useState(initialFilterValues);
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState(INVENTORY_VIEW_MODES.detailed);
  const [previewImage, setPreviewImage] = useState(null);
  const suggestions = useMemo(
    () => buildInventorySearchSuggestions(releasableInventoryItems),
    [releasableInventoryItems]
  );
  const filteredItems = useMemo(
    () => filterInventoryItems(releasableInventoryItems, appliedFilters),
    [appliedFilters, releasableInventoryItems]
  );
  const supplierStockItems = useMemo(
    () => buildSupplierStockBreakdown(filteredItems),
    [filteredItems]
  );
  const simpleTotalItems = supplierStockItems.length;
  const simpleTotalPages = Math.max(1, Math.ceil(simpleTotalItems / INVENTORY_SIMPLE_PAGE_SIZE));
  const simpleNormalizedPage = Math.min(currentPage, simpleTotalPages);
  const simpleStartIndex = (simpleNormalizedPage - 1) * INVENTORY_SIMPLE_PAGE_SIZE;
  const simpleEndIndex = Math.min(simpleStartIndex + INVENTORY_SIMPLE_PAGE_SIZE, simpleTotalItems);
  const paginatedSupplierStockItems = supplierStockItems.slice(simpleStartIndex, simpleEndIndex);
  const totalItems = filteredItems.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / INVENTORY_PAGE_SIZE));
  const normalizedPage = Math.min(currentPage, totalPages);
  const startIndex = (normalizedPage - 1) * INVENTORY_PAGE_SIZE;
  const endIndex = Math.min(startIndex + INVENTORY_PAGE_SIZE, totalItems);
  const paginatedItems = filteredItems.slice(startIndex, endIndex);
  const toasts = [
    loadError ? { id: 'inventory-load-error', tone: 'error', message: loadError } : null,
  ].filter(Boolean);
  const pagination = {
    currentPage: normalizedPage,
    endItem: endIndex,
    isNextDisabled: normalizedPage >= totalPages,
    isPreviousDisabled: normalizedPage <= 1,
    onNext: () => setCurrentPage((page) => Math.min(page + 1, totalPages)),
    onPrevious: () => setCurrentPage((page) => Math.max(page - 1, 1)),
    startItem: totalItems ? startIndex + 1 : 0,
    totalItems,
    totalPages,
  };
  const simplePagination = {
    currentPage: simpleNormalizedPage,
    endItem: simpleEndIndex,
    isNextDisabled: simpleNormalizedPage >= simpleTotalPages,
    isPreviousDisabled: simpleNormalizedPage <= 1,
    onNext: () => setCurrentPage((page) => Math.min(page + 1, simpleTotalPages)),
    onPrevious: () => setCurrentPage((page) => Math.max(page - 1, 1)),
    startItem: simpleTotalItems ? simpleStartIndex + 1 : 0,
    totalItems: simpleTotalItems,
    totalPages: simpleTotalPages,
  };

  function handleFilterChange(event) {
    const { name, value } = event.target;

    setFilterValues((currentValues) => ({
      ...currentValues,
      [name]: value,
    }));

    if (name === 'search' || name === 'warehouseLocation') {
      setAppliedFilters((currentValues) => ({
        ...currentValues,
        [name]: value,
      }));
      setCurrentPage(1);
    }
  }

  function handleFilterSubmit(event) {
    event.preventDefault();
    setAppliedFilters(filterValues);
    setCurrentPage(1);
  }

  function handleFilterReset() {
    setFilterValues(initialFilterValues);
    setAppliedFilters(initialFilterValues);
    setCurrentPage(1);
  }

  function handleViewModeChange(nextViewMode) {
    setViewMode(nextViewMode);
    setCurrentPage(1);
  }

  return (
    <AppShell
      navigationItems={navigationItems}
      activePath={activePath}
      onNavigate={onNavigate}
    >
      <ToastStack toasts={toasts} />
      <section className="mt-5 px-4 sm:mt-6 sm:px-6 lg:px-[26px]">
        <div className="grid gap-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="m-0 text-[1.25rem] font-semibold tracking-[-0.03em] text-[var(--theme-text)] sm:text-[1.4rem]">
                Inventory
              </h2>
              <p className="m-0 mt-2 text-[0.96rem] text-[var(--theme-text-muted)]">
                Review actual stock received from purchase orders, with receipt details for traceability.
              </p>
            </div>
            <InventoryViewSwitch value={viewMode} onChange={handleViewModeChange} />
          </div>

          <InventoryFilters
            values={filterValues}
            suggestions={suggestions}
            warehouseOptions={buildWarehouseOptions(groupedInventoryItems)}
            onChange={handleFilterChange}
            onSubmit={handleFilterSubmit}
            onReset={handleFilterReset}
          />

          {isLoading ? (
            <FeedbackMessage tone="info" message="Loading inventory records..." />
          ) : (
            <div className="grid gap-3">
              {viewMode === INVENTORY_VIEW_MODES.simple ? (
                <InventorySimpleView items={paginatedSupplierStockItems} />
              ) : (
                <InventoryTable items={paginatedItems} onPreviewImage={setPreviewImage} />
              )}
              {viewMode === INVENTORY_VIEW_MODES.detailed && totalItems ? (
                <div className="overflow-hidden rounded-[18px] border border-[var(--theme-border-soft)]">
                  <PaginationFooter {...pagination} />
                </div>
              ) : null}
              {viewMode === INVENTORY_VIEW_MODES.simple &&
              simpleTotalItems > INVENTORY_SIMPLE_PAGE_SIZE ? (
                <div className="overflow-hidden rounded-[18px] border border-[var(--theme-border-soft)]">
                  <PaginationFooter {...simplePagination} />
                </div>
              ) : null}
            </div>
          )}
        </div>
      </section>

      {previewImage ? (
        <ReceiptImagePreviewModal
          image={previewImage}
          onClose={() => setPreviewImage(null)}
        />
      ) : null}
    </AppShell>
  );
}

function InventoryViewSwitch({ value, onChange }) {
  return (
    <div className="inline-flex w-fit rounded-full border border-[var(--theme-border)] bg-white p-1 shadow-[0_10px_24px_rgba(var(--theme-shadow-rgb),0.06)]">
      {[
        { value: INVENTORY_VIEW_MODES.detailed, label: 'Detailed' },
        { value: INVENTORY_VIEW_MODES.simple, label: 'Simple' },
      ].map((option) => {
        const isActive = value === option.value;

        return (
          <button
            key={option.value}
            className={`min-h-[38px] rounded-full px-4 text-[0.84rem] font-semibold transition ${
              isActive
                ? 'bg-[var(--theme-accent-panel)] text-[var(--theme-accent-panel-text)] shadow-[0_8px_18px_rgba(var(--theme-shadow-rgb),0.12)]'
                : 'text-[var(--theme-text-muted)] hover:bg-[var(--theme-surface-soft)] hover:text-[var(--theme-text-strong)]'
            }`}
            type="button"
            onClick={() => onChange(option.value)}
            aria-pressed={isActive}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function InventoryFilters({ values, suggestions, warehouseOptions = [], onChange, onSubmit, onReset }) {
  return (
    <FilterPanel
      onSubmit={onSubmit}
      onReset={onReset}
      layoutClassName="grid gap-2.5 md:grid-cols-2 xl:grid-cols-[minmax(300px,1.2fr)_190px_220px_190px]"
    >
      <FilterField
        label="Search"
        name="search"
        type="search"
        value={values.search}
        onChange={onChange}
        placeholder="Search P.O. code, supplier, warehouse, remarks"
        suggestions={suggestions}
      />
      <FilterField
        label="Warehouse"
        name="warehouseLocation"
        value={values.warehouseLocation}
        onChange={onChange}
        options={warehouseOptions}
      />
      <FilterField
        label="Received Date"
        name="date"
        type="date"
        value={values.date}
        onChange={onChange}
      />
    </FilterPanel>
  );
}

function InventorySimpleView({ items }) {
  return (
    <section className="rounded-[22px] border border-[var(--theme-border)] bg-white p-4 shadow-[0_14px_30px_rgba(var(--theme-shadow-rgb),0.08)] sm:p-5">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="m-0 text-[0.82rem] text-[var(--theme-text-muted)] sm:text-sm">
            Available stock breakdown
          </p>
          <strong className="mt-1 block text-[1.25rem] font-semibold text-[var(--theme-text)]">
            Seaweeds Stocks by Sacks Mark
          </strong>
        </div>
        <span className="rounded-full border border-[var(--theme-border)] bg-[var(--theme-surface-muted)] px-3 py-1 text-[0.72rem] font-semibold uppercase tracking-[0.08em] text-[var(--theme-text-subtle)]">
          Filtered Inventory
        </span>
      </div>

      {items.length ? (
        <div className="grid gap-2">
          {items.map((item) => (
            <div
              key={item.id}
              className="flex items-center justify-between gap-4 rounded-[16px] border border-[var(--theme-border-soft)] bg-[var(--theme-surface-muted)] px-4 py-3"
            >
              <span className="min-w-0 truncate text-[0.94rem] font-medium text-[var(--theme-text-strong)]">
                {item.sacksMark}
              </span>
              <div className="flex shrink-0 flex-wrap justify-end gap-2 text-right">
                <span className="rounded-full border border-[var(--theme-border)] bg-white px-3 py-1 text-[0.82rem] font-semibold text-[var(--theme-text-subtle)]">
                  {formatQuantity(item.quantity, item.quantityUnit)}
                </span>
                <strong className="rounded-full border border-[var(--theme-border)] bg-[var(--theme-accent-soft)] px-3 py-1 text-[0.88rem] font-semibold text-[var(--theme-text)]">
                  {formatKgNet(item.quantityKg)}
                </strong>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-[18px] border border-dashed border-[var(--theme-border)] bg-[var(--theme-surface-muted)] px-5 py-12">
          <EmptyState />
        </div>
      )}
    </section>
  );
}

function InventoryTable({ items, onPreviewImage }) {
  return (
    <div className="relative overflow-hidden rounded-[22px] border border-[var(--theme-border)] bg-white shadow-[0_14px_30px_rgba(var(--theme-shadow-rgb),0.08)]">
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full min-w-[1460px] border-collapse">
          <thead>
            <tr className="bg-[var(--theme-accent-panel)]">
              <TableHeading>P.O. Code</TableHeading>
              <TableHeading>Sacks Mark</TableHeading>
              <TableHeading>Warehouse</TableHeading>
              <TableHeading>Received Date</TableHeading>
              <TableHeading>Type</TableHeading>
              <TableHeading>Quantity</TableHeading>
              <TableHeading>KG Net</TableHeading>
              <TableHeading>Released Sacks</TableHeading>
              <TableHeading>Released KG Net</TableHeading>
              <TableHeading>Available Sacks</TableHeading>
              <TableHeading>Available KG Net</TableHeading>
              <TableHeading>Unit Cost</TableHeading>
              <TableHeading>Sub Total</TableHeading>
              <TableHeading>Details</TableHeading>
            </tr>
          </thead>
          <tbody>
            {items.length ? (
              items.map((item) => (
                <InventoryRow
                  key={item.id}
                  item={item}
                  onPreviewImage={onPreviewImage}
                />
              ))
            ) : (
              <TableEmptyOverlay.Row colSpan={14} />
            )}
          </tbody>
        </table>
      </div>
      {!items.length ? (
        <TableEmptyOverlay>
          <EmptyState />
        </TableEmptyOverlay>
      ) : null}

      <div className="grid gap-3 p-4 md:hidden">
        {items.length ? (
          items.map((item) => (
            <InventoryCard
              key={item.id}
              item={item}
              onPreviewImage={onPreviewImage}
            />
          ))
        ) : (
          <div className="rounded-[18px] border border-dashed border-[var(--theme-border)] bg-[var(--theme-surface-muted)] px-5 py-12">
            <EmptyState />
          </div>
        )}
      </div>
    </div>
  );
}

function InventoryRow({ item, onPreviewImage }) {
  const note = splitReceivingNote(item.receiptDetailsSourceRemarks || item.remarks);
  const sourcePoCodes = getMergedSourcePoCodeDisplay(item);
  const canOpenSourceDetails = Boolean(item.isMergedInventory && sourcePoCodes.label);

  return (
    <tr className="border-t border-[var(--theme-border-soft)] transition-colors hover:bg-[var(--theme-surface-soft)]">
      <TableCell>
        <span className="block font-medium">{formatPurchaseOrderCode(item)}</span>
        <SourcePoCodesSummary
          canOpen={canOpenSourceDetails}
          item={item}
          onPreviewImage={onPreviewImage}
          sourcePoCodes={sourcePoCodes}
        />
      </TableCell>
      <TableCell>
        <span
          className="block max-w-[140px] truncate"
          title={formatSacksMarkDisplay(item)}
        >
          {formatSacksMarkDisplay(item)}
        </span>
      </TableCell>
      <TableCell>
        <span className="block max-w-[150px] truncate" title={formatWarehouseLocation(item)}>
          {formatWarehouseLocation(item)}
        </span>
      </TableCell>
      <TableCell>{formatShortDate(item.receivedAt)}</TableCell>
      <TableCell>
        <span className="block max-w-[140px] truncate" title={formatSeaweedType(item)}>
          {formatSeaweedType(item)}
        </span>
      </TableCell>
      <TableCell>{formatInventoryQuantity(item, 'quantity')}</TableCell>
      <TableCell>{formatKgNet(item.quantityKg)}</TableCell>
      <TableCell>
        <InventoryReleasedValue>{formatReleasedInventoryQuantity(item)}</InventoryReleasedValue>
      </TableCell>
      <TableCell>
        <InventoryReleasedValue>{formatKgNet(item.releasedKg)}</InventoryReleasedValue>
      </TableCell>
      <TableCell>
        <InventoryHighlightValue>{formatAvailableInventoryQuantity(item)}</InventoryHighlightValue>
      </TableCell>
      <TableCell>
        <InventoryHighlightValue>{formatKgNet(getAvailableKgNet(item))}</InventoryHighlightValue>
      </TableCell>
      <TableCell>{formatUnitCost(item.unitCost)}</TableCell>
      <TableCell>{formatInventorySubtotal(getAvailableSubtotal(item))}</TableCell>
      <TableCell>
        {item.isMergedInventory || note.receiptDetails || item.image ? (
          <ReceiptDetails
            details={note.receiptDetails}
            imagePath={item.image}
            item={item}
            remarks={note.remarks}
            onPreviewImage={onPreviewImage}
            title={formatPurchaseOrderCode(item)}
          />
        ) : '-'}
      </TableCell>
    </tr>
  );
}

function InventoryCard({ item, onPreviewImage }) {
  const note = splitReceivingNote(item.receiptDetailsSourceRemarks || item.remarks);
  const sourcePoCodes = getMergedSourcePoCodeDisplay(item, 3);
  const canOpenSourceDetails = Boolean(item.isMergedInventory && sourcePoCodes.label);

  return (
    <article className="rounded-[18px] border border-[var(--theme-border)] bg-[var(--theme-surface-muted)] p-3 transition duration-150 hover:-translate-y-0.5 hover:bg-[var(--theme-surface-soft)] hover:shadow-[0_12px_28px_rgba(var(--theme-shadow-rgb),0.11)]">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <div className="min-w-0">
          <strong className="block text-[1rem] text-[var(--theme-text)]">
            {formatPurchaseOrderCode(item)}
          </strong>
          <SourcePoCodesSummary
            canOpen={canOpenSourceDetails}
            className="mt-1 max-w-[240px] text-[0.78rem]"
            item={item}
            onPreviewImage={onPreviewImage}
            sourcePoCodes={sourcePoCodes}
          />
          <span className="mt-1 block text-[0.92rem] text-[var(--theme-text-muted)]">
            {formatSacksMarkDisplay(item)}
          </span>
          </div>
        </div>
        <span className="rounded-full border border-[var(--theme-border)] bg-white px-3 py-1 text-[0.72rem] font-semibold text-[var(--theme-text-subtle)]">
          {item.category}
        </span>
      </div>

      <div className="mt-3 grid gap-1.5 text-[0.88rem] text-[var(--theme-text-subtle)]">
        <RecordMeta label="Received Date" value={formatShortDate(item.receivedAt)} />
        <RecordMeta label="Warehouse" value={formatWarehouseLocation(item)} />
        <RecordMeta label="Type" value={formatSeaweedType(item)} />
        <RecordMeta label="Quantity" value={formatInventoryQuantity(item, 'quantity')} />
        <RecordMeta label="KG Net" value={formatKgNet(item.quantityKg)} />
        <RecordMeta
          label="Released Sacks"
          value={<InventoryReleasedValue>{formatReleasedInventoryQuantity(item)}</InventoryReleasedValue>}
        />
        <RecordMeta
          label="Released KG Net"
          value={<InventoryReleasedValue>{formatKgNet(item.releasedKg)}</InventoryReleasedValue>}
        />
        <RecordMeta
          label="Available Sacks"
          value={<InventoryHighlightValue>{formatAvailableInventoryQuantity(item)}</InventoryHighlightValue>}
        />
        <RecordMeta
          label="Available KG Net"
          value={<InventoryHighlightValue>{formatKgNet(getAvailableKgNet(item))}</InventoryHighlightValue>}
        />
        <RecordMeta label="Unit Cost" value={formatUnitCost(item.unitCost)} />
        <RecordMeta label="Sub Total" value={formatInventorySubtotal(getAvailableSubtotal(item))} />
        <RecordMeta
          label="Receipt Details"
          value={
            item.isMergedInventory || note.receiptDetails || item.image ? (
              <ReceiptDetails
                details={note.receiptDetails}
                imagePath={item.image}
                item={item}
                remarks={note.remarks}
                onPreviewImage={onPreviewImage}
                title={formatPurchaseOrderCode(item)}
              />
            ) : '-'
          }
        />
      </div>
    </article>
  );
}

function SourcePoCodesSummary({
  canOpen,
  className = 'mt-0.5 max-w-[150px] text-[0.72rem]',
  item,
  onPreviewImage,
  sourcePoCodes,
}) {
  if (!sourcePoCodes.label) {
    return null;
  }

  const content = `Sources: ${sourcePoCodes.label}`;
  const title = `Source P.O.s: ${sourcePoCodes.title}`;
  const commonClassName = `${className} block truncate font-medium`;

  if (!canOpen) {
    return (
      <span className={`${commonClassName} text-[var(--theme-text-muted)]`} title={title}>
        {content}
      </span>
    );
  }

  return (
    <button
      className={`${commonClassName} text-left text-[var(--theme-accent)] underline-offset-2 transition hover:text-[var(--theme-accent-strong)] hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--theme-accent)]`}
      type="button"
      title={`${title}. Click to review all source P.O.s.`}
      aria-label={`Review source P.O.s for ${formatPurchaseOrderCode(item)}`}
      onClick={() =>
        onPreviewImage({
          title: formatPurchaseOrderCode(item),
          item,
        })
      }
    >
      {content}
    </button>
  );
}

function ReceiptDetails({ details, imagePath, item, remarks, onPreviewImage, title }) {
  const imageUrl = buildAssetUrl(imagePath);

  return (
    <button
      className="inline-flex min-h-[36px] items-center justify-center gap-1.5 rounded-full border border-[var(--theme-border)] bg-white px-3 py-1.5 text-[0.78rem] font-semibold text-[var(--theme-text-subtle)] transition hover:border-[var(--theme-accent)] hover:bg-[var(--theme-surface-soft)]"
      type="button"
      onClick={() => onPreviewImage({ url: imageUrl, title, details, item, remarks })}
    >
      <ImageIcon className="h-3.5 w-3.5" aria-hidden="true" />
      {item?.isMergedInventory ? 'Review merge' : 'View details'}
    </button>
  );
}

function MergeCalculationDetails({ items, summary }) {
  return (
    <div className="border-t border-[var(--theme-border-soft)] bg-[var(--theme-app-bg)] px-4 py-3">
      <div className="grid gap-3 text-[0.84rem] leading-6 text-[var(--theme-text-subtle)]">
        <div className="rounded-[14px] border border-[var(--theme-border-soft)] bg-white p-3">
          <strong className="block text-[0.78rem] font-semibold uppercase tracking-[0.08em] text-[var(--theme-text-muted)]">
            Formula
          </strong>
          <p className="m-0 mt-2 font-medium text-[var(--theme-text-strong)]">
            Merged Unit Cost = Total Sub Total / Total KG Net
          </p>
        </div>

        <div className="rounded-[14px] border border-[var(--theme-border-soft)] bg-white p-3">
          <strong className="block text-[0.78rem] font-semibold uppercase tracking-[0.08em] text-[var(--theme-text-muted)]">
            Source Computation
          </strong>
          <div className="mt-2 grid gap-1.5">
            {items.map((item) => (
              <div
                key={getInventorySelectionId(item)}
                className="grid gap-1 rounded-[12px] bg-[var(--theme-surface-soft)] px-3 py-2 sm:grid-cols-[120px_minmax(0,1fr)]"
              >
                <span className="font-semibold text-[var(--theme-text-strong)]">
                  {formatPurchaseOrderCode(item)}
                </span>
                <span>
                  {formatKgNet(item.quantityKg)} x {formatUnitCost(item.unitCost)} = {formatInventorySubtotal(item.subtotal)}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-[14px] border border-[var(--theme-border-soft)] bg-white p-3">
          <strong className="block text-[0.78rem] font-semibold uppercase tracking-[0.08em] text-[var(--theme-text-muted)]">
            Solution
          </strong>
          <div className="mt-2 grid gap-1.5">
            <CalculationLine label="Total KG Net" value={formatKgNet(summary.totalKgNet)} />
            <CalculationLine label="Total Sub Total" value={formatInventorySubtotal(summary.totalSubtotal)} />
            <CalculationLine
              label="Merged Unit Cost"
              value={`${formatInventorySubtotal(summary.totalSubtotal)} / ${formatNumber(summary.totalKgNet)} kg = ${formatUnitCost(summary.weightedUnitCost)}`}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function CalculationLine({ label, value }) {
  return (
    <div className="grid gap-1 rounded-[12px] bg-[var(--theme-surface-soft)] px-3 py-2 sm:grid-cols-[150px_minmax(0,1fr)]">
      <span className="font-semibold text-[var(--theme-text-muted)]">{label}</span>
      <span className="font-medium text-[var(--theme-text-strong)]">{value}</span>
    </div>
  );
}

function mapMergedSourceToCalculationItem(source) {
  return {
    id: source.purchaseOrderId,
    purchaseOrderId: source.purchaseOrderId,
    poCode: source.poCode,
    sacksMark: formatSourceSacksMark(source),
    companyName: source.supplierLabel,
    dryQuantity: source.dryQuantity,
    wetQuantity: source.wetQuantity,
    quantity: source.totalQuantity,
    quantityUnit: source.quantityUnit,
    moistureContent: source.moistureContent,
    quantityKg: source.kgNet,
    unitCost: source.unitCost,
    subtotal: source.subtotal,
  };
}

function MergeSummaryTile({ label, value }) {
  return (
    <div className="rounded-[14px] border border-[var(--theme-border-soft)] bg-[var(--theme-app-bg)] p-3">
      <span className="block text-[0.72rem] font-semibold uppercase tracking-[0.08em] text-[var(--theme-text-muted)]">
        {label}
      </span>
      <strong className="mt-1 block break-words text-[0.98rem] text-[var(--theme-text-strong)]">
        {value}
      </strong>
    </div>
  );
}

function ReceiptImagePreviewModal({ image, onClose }) {
  const [zoom, setZoom] = useState(1);
  const [now, setNow] = useState(() => new Date());
  const zoomPercent = Math.round(zoom * 100);
  const receiptFields = parseReceiptDetails(image.details);
  const isMergedInventory = Boolean(image.item?.isMergedInventory);
  const shouldShowZoomControls = Boolean(image.url);
  const receiptImageRetentionDays = getReceiptImageRetentionDays(image.item);
  const receiptImageRetention = getReceiptImageRetention(image.item, now);

  useEffect(() => {
    if (!image.item?.imageExpiresAt) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setNow(new Date());
    }, 1000);

    return () => window.clearInterval(timer);
  }, [image.item?.imageExpiresAt]);

  function handleZoomOut() {
    setZoom((currentZoom) => Math.max(0.5, Math.round((currentZoom - 0.25) * 100) / 100));
  }

  function handleZoomIn() {
    setZoom((currentZoom) => Math.min(3, Math.round((currentZoom + 0.25) * 100) / 100));
  }

  return (
    <ModalBackdrop
      backdropClassName="bg-[rgba(33,27,22,0.55)]"
      contentClassName="!px-4"
      zIndexClassName="z-50"
    >
      <div className="mx-auto w-full max-w-5xl rounded-[22px] border border-[var(--theme-border)] bg-white p-3 shadow-[0_24px_70px_rgba(var(--theme-shadow-rgb),0.32)]">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-3 px-1">
          <strong className="text-[0.95rem] text-[var(--theme-text)]">
            {image.title || 'Receipt image'}
          </strong>
          <div className="flex items-center gap-2">
            {shouldShowZoomControls ? (
              <div className="inline-flex items-center overflow-hidden rounded-full border border-[var(--theme-border)] bg-white">
                <button
                  className="inline-flex h-9 w-9 items-center justify-center text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)] disabled:cursor-not-allowed disabled:text-[var(--theme-text-muted)]"
                  type="button"
                  onClick={handleZoomOut}
                  disabled={zoom <= 0.5}
                  aria-label="Zoom out receipt image"
                  title="Zoom out"
                >
                  <Minus className="h-4 w-4" aria-hidden="true" />
                </button>
                <span className="min-w-[54px] border-x border-[var(--theme-border-soft)] px-2 text-center text-[0.78rem] font-semibold text-[var(--theme-text-subtle)]">
                  {zoomPercent}%
                </span>
                <button
                  className="inline-flex h-9 w-9 items-center justify-center text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)] disabled:cursor-not-allowed disabled:text-[var(--theme-text-muted)]"
                  type="button"
                  onClick={handleZoomIn}
                  disabled={zoom >= 3}
                  aria-label="Zoom in receipt image"
                  title="Zoom in"
                >
                  <Plus className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            ) : null}
            <button
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[var(--theme-border)] bg-white text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)]"
              type="button"
              onClick={onClose}
              aria-label="Close receipt image preview"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>
        <div className="grid max-h-[78vh] gap-3 overflow-y-auto">
          {isMergedInventory ? (
            <MergedInventoryDetails item={image.item} />
          ) : null}
          {image.url ? (
            <div className="relative max-h-[58vh] overflow-auto rounded-[16px] border border-[var(--theme-border-soft)] bg-[var(--theme-surface-soft)] p-2">
              <ReceiptImageRetentionBadge retention={receiptImageRetention} />
              <img
                className="mx-auto block h-auto max-w-none rounded-[12px] object-contain"
                src={image.url}
                alt={`${image.title || 'Receipt'} full preview`}
                style={{ width: `${zoom * 100}%` }}
              />
            </div>
          ) : image.details && !isMergedInventory ? (
            <ReceiptImageExpiredNotice retentionDays={receiptImageRetentionDays} />
          ) : null}
          {image.details && !isMergedInventory ? (
            <div className="rounded-[16px] border border-[var(--theme-border-soft)] bg-[var(--theme-app-bg)] p-3">
              <strong className="block text-[0.86rem] text-[var(--theme-text-strong)]">
                Receipt Details
              </strong>
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                <div className="rounded-[14px] border border-[var(--theme-border-soft)] bg-white p-3">
                  <strong className="block text-[0.78rem] font-semibold uppercase tracking-[0.08em] text-[var(--theme-text-muted)]">
                    Captured Fields
                  </strong>
                  <div className="mt-2 grid gap-1.5 text-[0.8rem] text-[var(--theme-text-subtle)]">
                    {receiptFields.length ? (
                      receiptFields.map((field) => (
                        <div
                          key={`${field.label}-${field.value}`}
                          className="grid grid-cols-[112px_minmax(0,1fr)] gap-2"
                        >
                          <span className="font-semibold text-[var(--theme-text-muted)]">{field.label}</span>
                          <span className="break-words font-medium text-[var(--theme-text-strong)]">
                            {field.value || '-'}
                          </span>
                        </div>
                      ))
                    ) : (
                      <pre className="m-0 max-h-56 overflow-y-auto whitespace-pre-wrap text-[0.8rem] leading-5 text-[var(--theme-text-subtle)]">
                        {image.details}
                      </pre>
                    )}
                  </div>
                </div>

                <div className="rounded-[14px] border border-[var(--theme-border-soft)] bg-white p-3">
                  <strong className="block text-[0.78rem] font-semibold uppercase tracking-[0.08em] text-[var(--theme-text-muted)]">
                    Remarks
                  </strong>
                  <p className="m-0 mt-2 whitespace-pre-wrap text-[0.84rem] leading-5 text-[var(--theme-text-subtle)]">
                    {image.remarks || '-'}
                  </p>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </ModalBackdrop>
  );
}

function MergedInventoryDetails({ item }) {
  const [isCalculationOpen, setIsCalculationOpen] = useState(false);
  const [selectedSourceDetails, setSelectedSourceDetails] = useState(null);
  const sourceDetailsRef = useRef(null);
  const sources = Array.isArray(item.sourcePurchaseOrders) ? item.sourcePurchaseOrders : [];
  const calculationItems = sources.map(mapMergedSourceToCalculationItem);
  const calculationSummary = buildMergedInventorySummary([
    {
      ...item,
      dryQuantity: 0,
      wetQuantity: 0,
      quantity: 0,
      quantityUnit: 'kg',
    },
  ]);

  useEffect(() => {
    if (!selectedSourceDetails) {
      return;
    }

    window.requestAnimationFrame(() => {
      sourceDetailsRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    });
  }, [selectedSourceDetails]);

  return (
    <div className="rounded-[16px] border border-[var(--theme-border-soft)] bg-[var(--theme-app-bg)] p-3">
      <strong className="block text-[0.86rem] text-[var(--theme-text-strong)]">
        Merged Inventory Details
      </strong>
      <div className="mt-3 grid gap-3 md:grid-cols-3">
        <MergeSummaryTile label="Warehouse" value={formatWarehouseLocation(item)} />
        <MergeSummaryTile label="Sacks Mark" value={formatSacksMarkDisplay(item)} />
        <MergeSummaryTile label="MC %" value={formatMoistureContent(item.moistureContent)} />
        <MergeSummaryTile label="Total Quantity" value={formatInventoryQuantity(item, 'quantity')} />
        <MergeSummaryTile label="KG Net" value={formatKgNet(item.quantityKg)} />
        <MergeSummaryTile label="Unit Cost" value={formatUnitCost(item.unitCost)} />
        <MergeSummaryTile label="Sub Total" value={formatInventorySubtotal(item.subtotal)} />
      </div>

      <div className="mt-4 overflow-hidden rounded-[14px] border border-[var(--theme-border-soft)] bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] border-collapse">
            <thead>
              <tr className="bg-[var(--theme-accent-panel)]">
                <TableHeading>Source P.O.</TableHeading>
                <TableHeading>Sacks Mark</TableHeading>
                <TableHeading>Category</TableHeading>
                <TableHeading>Sacks</TableHeading>
                <TableHeading>MC %</TableHeading>
                <TableHeading>KG Net</TableHeading>
                <TableHeading>Unit Cost</TableHeading>
                <TableHeading>Sub Total</TableHeading>
                <TableHeading>View Details</TableHeading>
              </tr>
            </thead>
            <tbody>
              {sources.length ? (
                sources.map((source, sourceIndex) => {
                  const sourceDetails = splitReceivingNote(source.remarks);
                  const hasDetails = Boolean(
                    sourceDetails.remarks || sourceDetails.receiptDetails || source.image
                  );

                  return (
                    <tr
                      key={`${item.id}-${source.purchaseOrderId}-${source.category}-${sourceIndex}`}
                      className="border-t border-[var(--theme-border-soft)]"
                    >
                      <TableCell>{source.poCode || '-'}</TableCell>
                      <TableCell>
                        <span className="block max-w-[160px] truncate" title={formatSourceSacksMark(source)}>
                          {formatSourceSacksMark(source)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <InventoryCategoryBadge category={source.category} />
                      </TableCell>
                      <TableCell>{formatQuantity(source.totalQuantity, source.quantityUnit)}</TableCell>
                      <TableCell>{formatMoistureContent(source.moistureContent)}</TableCell>
                      <TableCell>{formatKgNet(source.kgNet)}</TableCell>
                      <TableCell>{formatUnitCost(source.unitCost)}</TableCell>
                      <TableCell>{formatInventorySubtotal(source.subtotal)}</TableCell>
                      <TableCell>
                        <button
                          className="inline-flex min-h-[32px] items-center justify-center rounded-full border border-[var(--theme-border)] bg-white px-3 text-[0.76rem] font-semibold text-[var(--theme-text-subtle)] transition hover:border-[var(--theme-accent)] hover:bg-[var(--theme-surface-soft)] disabled:cursor-not-allowed disabled:opacity-50"
                          type="button"
                          disabled={!hasDetails}
                          onClick={() => setSelectedSourceDetails(source)}
                        >
                          View Details
                        </button>
                      </TableCell>
                    </tr>
                  );
                })
              ) : (
                <tr className="border-t border-[var(--theme-border-soft)]">
                  <td className="px-4 py-8 text-center text-[0.84rem] text-[var(--theme-text-muted)]" colSpan={9}>
                    No source P.O. details found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectedSourceDetails ? (
        <div ref={sourceDetailsRef}>
          <MergedSourceDetailsPanel
            source={selectedSourceDetails}
            onClose={() => setSelectedSourceDetails(null)}
          />
        </div>
      ) : null}

      <div className="mt-4 overflow-hidden rounded-[14px] border border-[var(--theme-border)] bg-white">
        <button
          className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition hover:bg-[var(--theme-surface-soft)]"
          type="button"
          onClick={() => setIsCalculationOpen((isOpen) => !isOpen)}
          aria-expanded={isCalculationOpen}
        >
          <span>
            <strong className="block text-[0.9rem] text-[var(--theme-text-strong)]">
              Unit Cost Formula
            </strong>
            <span className="mt-0.5 block text-[0.78rem] text-[var(--theme-text-muted)]">
              View the weighted-average solution saved for this merge
            </span>
          </span>
          <span className="shrink-0 text-[1.1rem] font-semibold text-[var(--theme-text-subtle)]">
            {isCalculationOpen ? '-' : '+'}
          </span>
        </button>
        {isCalculationOpen ? (
          <MergeCalculationDetails
            items={calculationItems}
            summary={calculationSummary}
          />
        ) : null}
      </div>
    </div>
  );
}

function MergedSourceDetailsPanel({ source, onClose }) {
  const note = splitReceivingNote(source.remarks);
  const receiptFields = parseReceiptDetails(note.receiptDetails);
  const receiptImageRetentionDays = getReceiptImageRetentionDays(source);
  const receiptImageRetention = getReceiptImageRetention(source);

  return (
    <div className="mt-4 rounded-[14px] border border-[var(--theme-border-soft)] bg-white p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <strong className="block text-[0.9rem] text-[var(--theme-text-strong)]">
            {source.poCode || 'Source P.O.'} Details
          </strong>
          <span className="mt-0.5 block text-[0.78rem] text-[var(--theme-text-muted)]">
            {source.supplierLabel || '-'}{source.receivedAt ? ` - ${formatShortDate(source.receivedAt)}` : ''}
          </span>
        </div>
        <button
          className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[var(--theme-border)] bg-white text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)]"
          type="button"
          onClick={onClose}
          aria-label="Close source details"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      <div className="mt-3 grid gap-3 md:grid-cols-2">
        <div className="rounded-[12px] border border-[var(--theme-border-soft)] bg-[var(--theme-app-bg)] p-3">
          <strong className="block text-[0.78rem] font-semibold uppercase tracking-[0.08em] text-[var(--theme-text-muted)]">
            Receipt Details
          </strong>
          <div className="mt-2 grid gap-1.5 text-[0.8rem] text-[var(--theme-text-subtle)]">
            {receiptFields.length ? (
              receiptFields.map((field) => (
                <div
                  key={`${source.poCode}-${field.label}-${field.value}`}
                  className="grid grid-cols-[112px_minmax(0,1fr)] gap-2"
                >
                  <span className="font-semibold text-[var(--theme-text-muted)]">{field.label}</span>
                  <span className="break-words font-medium text-[var(--theme-text-strong)]">
                    {field.value || '-'}
                  </span>
                </div>
              ))
            ) : (
              <span className="text-[var(--theme-text-muted)]">No captured receipt fields found.</span>
            )}
          </div>
        </div>

        <div className="rounded-[12px] border border-[var(--theme-border-soft)] bg-[var(--theme-app-bg)] p-3">
          <strong className="block text-[0.78rem] font-semibold uppercase tracking-[0.08em] text-[var(--theme-text-muted)]">
            Remarks
          </strong>
          <p className="m-0 mt-2 whitespace-pre-wrap text-[0.84rem] leading-5 text-[var(--theme-text-subtle)]">
            {note.remarks || '-'}
          </p>
          {source.image ? (
            <div className="mt-3 grid justify-start gap-2">
              <ReceiptImageRetentionBadge
                className="static ml-0"
                retention={receiptImageRetention}
              />
              <a
                className="inline-flex text-[0.78rem] font-semibold text-[var(--theme-accent)] underline-offset-2 hover:text-[var(--theme-accent-strong)] hover:underline"
                href={buildAssetUrl(source.image)}
                target="_blank"
                rel="noreferrer"
              >
                Open receipt image
              </a>
            </div>
          ) : note.receiptDetails ? (
            <ReceiptImageExpiredNotice
              className="mt-3"
              retentionDays={receiptImageRetentionDays}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ReceiptImageRetentionBadge({ retention, className = '' }) {
  if (!retention?.label) {
    return null;
  }

  return (
    <span
      className={`sticky right-3 top-3 z-10 ml-auto flex w-max items-center rounded-full border px-3 py-1.5 text-[0.76rem] font-semibold shadow-[0_8px_22px_rgba(var(--theme-shadow-rgb),0.12)] ${
        retention.tone === 'danger'
          ? 'border-[#efc3b8] bg-[#fff6f3] text-[#a34334]'
          : retention.tone === 'warning'
            ? 'border-[#ead7a8] bg-[#fff8e6] text-[#8a6424]'
            : 'border-[#d6e6d7] bg-[#f4faf4] text-[#3f7545]'
      } ${className}`}
    >
      {retention.modalLabel || retention.label}
    </span>
  );
}

function ReceiptImageExpiredNotice({ retentionDays, className = '' }) {
  return (
    <div
      className={`rounded-[14px] border border-[#ead7a8] bg-[#fff8e6] px-3 py-2 text-[0.8rem] font-semibold text-[#8a6424] ${className}`}
    >
      Receipt image self-destructed after {retentionDays} days
    </div>
  );
}

function getReceiptImageRetentionDays(item) {
  return Number(item?.imageRetentionDays || 30);
}

function getReceiptImageRetention(item, now = new Date()) {
  const retentionDays = Number(item?.imageRetentionDays || 30);
  const daysRemaining = Number(item?.imageDaysRemaining);
  const expiresAt = item?.imageExpiresAt ? new Date(item.imageExpiresAt) : null;
  const remainingMs =
    expiresAt && !Number.isNaN(expiresAt.getTime())
      ? expiresAt.getTime() - now.getTime()
      : null;

  if (!item?.image || (!Number.isFinite(daysRemaining) && remainingMs === null)) {
    return {
      daysRemaining: null,
      retentionDays,
      label: '',
      tone: 'neutral',
    };
  }

  const normalizedDaysRemaining =
    remainingMs === null
      ? Math.max(0, Math.min(retentionDays, daysRemaining))
      : Math.max(0, Math.min(retentionDays, Math.ceil(remainingMs / MS_PER_DAY)));
  const countdownLabel = formatSelfDestructCountdown(remainingMs);

  return {
    daysRemaining: normalizedDaysRemaining,
    retentionDays,
    label:
      countdownLabel ||
      (normalizedDaysRemaining <= 0
        ? 'Deletes today'
        : `${normalizedDaysRemaining} day${normalizedDaysRemaining === 1 ? '' : 's'} left`),
    modalLabel:
      countdownLabel
        ? remainingMs <= 0
          ? countdownLabel
          : `${countdownLabel} to self-destruct`
        : normalizedDaysRemaining <= 0
          ? 'Self-destructs today'
          : `${normalizedDaysRemaining} day${normalizedDaysRemaining === 1 ? '' : 's'} left to self-destruct`,
    tone:
      normalizedDaysRemaining <= 3
        ? 'danger'
        : normalizedDaysRemaining <= 7
          ? 'warning'
          : 'success',
  };
}

function formatSelfDestructCountdown(remainingMs) {
  if (!Number.isFinite(remainingMs)) {
    return '';
  }

  if (remainingMs <= 0) {
    return 'Self-destructing now';
  }

  if (remainingMs >= MS_PER_DAY) {
    return '';
  }

  const totalSeconds = Math.ceil(remainingMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const parts = [];

  if (hours) {
    parts.push(`${hours}h`);
  }

  if (hours || minutes) {
    parts.push(`${minutes}m`);
  }

  parts.push(`${seconds}s`);

  return `${parts.join(' ')} left`;
}

function TableHeading({ children }) {
  return (
    <th className="whitespace-nowrap px-3 py-2.5 text-left text-[0.72rem] font-semibold uppercase tracking-[0.04em] text-[var(--theme-accent-panel-text)] lg:px-4">
      {children}
    </th>
  );
}

function TableCell({ children }) {
  return (
    <td className="whitespace-nowrap px-3 py-2 align-middle text-[0.84rem] text-[var(--theme-text)] lg:px-4">
      {children}
    </td>
  );
}

function InventoryHighlightValue({ children }) {
  return (
    <span className="inline-flex min-h-[30px] items-center rounded-full border border-[var(--theme-border)] bg-[var(--theme-accent-soft)] px-3 text-[0.84rem] font-semibold text-[var(--theme-text-subtle)] shadow-[inset_0_1px_0_rgba(255,255,255,0.72)]">
      {children}
    </span>
  );
}

function InventoryReleasedValue({ children }) {
  return (
    <span className="inline-flex min-h-[30px] items-center rounded-full border border-[#f0b8ad] bg-[#fff3f1] px-3 text-[0.84rem] font-semibold text-[#a33f31] shadow-[inset_0_1px_0_rgba(255,255,255,0.78)]">
      {children}
    </span>
  );
}

function InventoryCategoryBadge({ category }) {
  const normalizedCategory = String(category || '').trim();
  const categoryKey = normalizedCategory.toLowerCase();
  const categoryClassName =
    categoryKey === 'reject'
      ? 'border-[#f4a7b6] bg-[#fff0f3] text-[#b62546]'
      : categoryKey === 'wet'
        ? 'border-[#71cbd0] bg-[#e8fbfc] text-[#087981]'
        : 'border-[var(--theme-border-soft)] bg-[var(--theme-surface-soft)] text-[var(--theme-text-subtle)]';

  return (
    <span
      className={`inline-flex min-h-[28px] min-w-[76px] items-center justify-center rounded-full border px-3 text-[0.72rem] font-semibold ${categoryClassName}`}
    >
      {normalizedCategory || '-'}
    </span>
  );
}

function RecordMeta({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-[var(--theme-text-muted)]">{label}</span>
      <div className="text-right font-medium text-[var(--theme-text)]">{value}</div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center text-center text-[var(--theme-text-muted)]">
      <div className="grid h-14 w-14 place-items-center rounded-full bg-[var(--theme-accent-soft)] text-[var(--theme-text-muted)]">
        <ClipboardList className="h-6 w-6" aria-hidden="true" />
      </div>
      <strong className="mt-4 text-[1rem] font-medium text-[var(--theme-text-strong)]">
        No inventory records found
      </strong>
    </div>
  );
}

function filterInventoryItems(items, filters) {
  const normalizedSearch = String(filters.search || '').trim().toLowerCase();

  return items.filter((item) => {
    const note = splitReceivingNote(item.remarks);
    const matchesSearch =
      !normalizedSearch ||
      [
        formatPurchaseOrderCode(item),
        ...getMergedSourcePoCodes(item),
        item.companyName,
        item.supplierName,
        item.sacksMark,
        item.warehouseLocation,
        item.productName,
        item.category,
        item.moistureContent,
        formatInventoryQuantity(item, 'dryQuantity'),
        formatInventoryQuantity(item, 'wetQuantity'),
        formatInventoryQuantity(item, 'quantity'),
        formatKgNet(item.quantityKg),
        formatReleasedInventoryQuantity(item),
        formatKgNet(item.releasedKg),
        formatAvailableInventoryQuantity(item),
        formatKgNet(getAvailableKgNet(item)),
        note.remarks,
        note.receiptDetails,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(normalizedSearch));
    const matchesDate =
      !filters.date || toDateInputValue(item.receivedAt) === filters.date;
    const matchesWarehouse =
      filters.warehouseLocation === 'All' ||
      String(item.warehouseLocation || '').trim() === String(filters.warehouseLocation || '').trim();
    return matchesSearch && matchesDate && matchesWarehouse;
  });
}

function groupSamePurchaseOrderInventoryItems(items = []) {
  const groupedItems = [];
  const groupIndexesByPurchaseOrderId = new Map();

  items.forEach((item) => {
    if (!shouldGroupPurchaseOrderInventoryItem(item)) {
      groupedItems.push(item);
      return;
    }

    const groupKey = String(item.purchaseOrderId || '');
    const existingIndex = groupIndexesByPurchaseOrderId.get(groupKey);

    if (typeof existingIndex !== 'number') {
      groupIndexesByPurchaseOrderId.set(groupKey, groupedItems.length);
      groupedItems.push({
        ...item,
        id: `purchase-order-${groupKey}`,
        sourceType: 'purchase-order',
        sourceId: Number(item.purchaseOrderId),
        sourceKey: `purchase-order-${groupKey}`,
        category: item.category,
        quantity: Number(item.quantity || 0),
        quantityKg: Number(item.quantityKg || 0),
        releasedKg: Number(item.releasedKg || 0),
        availableKg: Number(item.availableKg ?? item.quantityKg ?? 0),
        subtotal: Number(item.subtotal || 0),
        dryQuantity: Number(item.dryQuantity || 0),
        wetQuantity: Number(item.wetQuantity || 0),
        rejectQuantity: 0,
        sourceInventoryItems: [item],
      });
      return;
    }

    groupedItems[existingIndex] = mergePurchaseOrderInventoryDisplayItem(
      groupedItems[existingIndex],
      item
    );
  });

  return groupedItems.map((item) =>
    Array.isArray(item.sourceInventoryItems) && item.sourceInventoryItems.length === 1
      ? item.sourceInventoryItems[0]
      : item
  );
}

function shouldGroupPurchaseOrderInventoryItem(item) {
  return (
    !item.isMergedInventory &&
    item.sourceType === 'receiving' &&
    item.purchaseOrderId &&
    item.category !== 'Reject'
  );
}

function mergePurchaseOrderInventoryDisplayItem(groupedItem, nextItem) {
  const sourceInventoryItems = [...(groupedItem.sourceInventoryItems || []), nextItem];
  const quantity = sumNumericField(sourceInventoryItems, 'quantity');
  const quantityKg = sumNumericField(sourceInventoryItems, 'quantityKg');
  const releasedKg = sumNumericField(sourceInventoryItems, 'releasedKg');
  const availableKg = sourceInventoryItems.reduce(
    (total, item) => total + getAvailableKgNet(item),
    0
  );
  const subtotal = sumNumericField(sourceInventoryItems, 'subtotal');
  const dryQuantity = sumNumericField(sourceInventoryItems, 'dryQuantity');
  const wetQuantity = sumNumericField(sourceInventoryItems, 'wetQuantity');
  const categories = Array.from(
    new Set(sourceInventoryItems.map((item) => item.category).filter(Boolean))
  );
  const receiptSourceItem =
    sourceInventoryItems.find((item) => item.image || item.receiptDetailsSourceRemarks || item.remarks) ||
    nextItem;

  return {
    ...groupedItem,
    category: categories.length > 1 ? categories.join(' + ') : categories[0] || groupedItem.category,
    quantity,
    quantityLabel: formatQuantity(quantity, groupedItem.quantityUnit),
    quantityKg,
    releasedKg,
    availableKg,
    subtotal,
    dryQuantity,
    wetQuantity,
    unitCost: quantityKg > 0 ? subtotal / quantityKg : groupedItem.unitCost,
    receiptDetailsSourceRemarks:
      receiptSourceItem.receiptDetailsSourceRemarks || receiptSourceItem.remarks || groupedItem.receiptDetailsSourceRemarks,
    remarks: receiptSourceItem.remarks || groupedItem.remarks,
    image: receiptSourceItem.image || groupedItem.image,
    sourceInventoryItems,
  };
}

function buildWarehouseOptions(items) {
  const warehouseLocations = Array.from(
    new Set(
      items
        .map((item) => String(item.warehouseLocation || '').trim())
        .filter(Boolean)
    )
  ).sort((firstValue, secondValue) => firstValue.localeCompare(secondValue));

  return [
    { value: 'All', label: 'All Warehouses' },
    ...warehouseLocations.map((warehouseLocation) => ({
      value: warehouseLocation,
      label: warehouseLocation,
    })),
  ];
}

function buildSupplierStockBreakdown(items) {
  const stocksBySacksMark = new Map();

  items.forEach((item) => {
    const sacksMark = formatSacksMarkDisplay(item);
    const sacksMarkKey = sacksMark.toLowerCase();
    const quantityUnit = getInventoryQuantityUnit(item);
    const currentStock = stocksBySacksMark.get(sacksMarkKey) || {
      id: sacksMarkKey,
      sacksMark,
      quantity: 0,
      quantityUnit,
      quantityKg: 0,
    };

    currentStock.quantity += getAvailableInventoryQuantity(item);
    currentStock.quantityKg += getAvailableKgNet(item);
    stocksBySacksMark.set(sacksMarkKey, currentStock);
  });

  return Array.from(stocksBySacksMark.values())
    .filter((item) => item.quantityKg > 0)
    .sort(
      (firstItem, secondItem) =>
        secondItem.quantityKg - firstItem.quantityKg ||
        firstItem.sacksMark.localeCompare(secondItem.sacksMark)
    );
}

function buildInventorySearchSuggestions(items) {
  const suggestionsByKey = new Map();

  items.forEach((item) => {
    const suggestionEntries = [
      ['po-code', formatPurchaseOrderCode(item), 'P.O. Code'],
      ...getMergedSourcePoCodes(item).map((poCode) => [
        'source-po-code',
        poCode,
        `Source P.O. merged in ${formatPurchaseOrderCode(item)}`,
      ]),
      ['supplier', item.companyName || item.supplierName, 'Supplier'],
      ['warehouse', item.warehouseLocation, 'Warehouse'],
    ];

    suggestionEntries.forEach(([type, value, detail]) => {
      const normalizedValue = String(value || '').trim();

      if (!normalizedValue) {
        return;
      }

      suggestionsByKey.set(`${type}:${normalizedValue.toLowerCase()}`, {
        group: type,
        value: normalizedValue,
        label: normalizedValue,
        detail,
      });
    });
  });

  return Array.from(suggestionsByKey.values())
    .sort(sortInventorySearchSuggestions);
}

function sortInventorySearchSuggestions(firstSuggestion, secondSuggestion) {
  const groupPriority = {
    'source-po-code': 0,
    'po-code': 1,
    supplier: 2,
    warehouse: 3,
  };
  const firstPriority = groupPriority[firstSuggestion.group] ?? 99;
  const secondPriority = groupPriority[secondSuggestion.group] ?? 99;

  return (
    firstPriority - secondPriority ||
    firstSuggestion.label.localeCompare(secondSuggestion.label)
  );
}

function getInventorySelectionId(item) {
  return String(item.purchaseOrderId || item.id);
}

function getAvailableKgNet(item) {
  const availableKg = Number(item.availableKg);

  return Number.isFinite(availableKg) ? availableKg : Number(item.quantityKg || 0);
}

function getAvailableSubtotal(item) {
  return getAvailableKgNet(item) * Number(item.unitCost || 0);
}

function getInventoryQuantityValue(item) {
  const label = String(item.quantityLabel || '').trim();
  const labelMatch = label.match(/[\d,.]+/);

  if (labelMatch) {
    return Number(labelMatch[0].replace(/,/g, ''));
  }

  return Number(item.quantity || 0);
}

function getInventoryQuantityUnit(item) {
  const label = String(item.quantityLabel || '').trim().toLowerCase();

  if (label.includes('sack')) {
    return 'sacks';
  }

  return String(item.quantityUnit || '').trim().toLowerCase() === 'sacks' ? 'sacks' : 'kg';
}

function getReleasedInventoryQuantity(item) {
  const releasedKg = Number(item.releasedKg || 0);
  const quantityUnit = getInventoryQuantityUnit(item);

  if (quantityUnit === 'kg') {
    return releasedKg;
  }

  const quantityKg = Number(item.quantityKg || 0);
  const quantity = getInventoryQuantityValue(item);
  const kgPerQuantity = quantityKg > 0 && quantity > 0 ? quantityKg / quantity : 1;

  return releasedKg / kgPerQuantity;
}

function getAvailableInventoryQuantity(item) {
  const availableKg = getAvailableKgNet(item);
  const quantityUnit = getInventoryQuantityUnit(item);

  if (quantityUnit === 'kg') {
    return availableKg;
  }

  const quantityKg = Number(item.quantityKg || 0);
  const quantity = getInventoryQuantityValue(item);
  const kgPerQuantity = quantityKg > 0 && quantity > 0 ? quantityKg / quantity : 1;

  return availableKg / kgPerQuantity;
}

function buildMergedInventorySummary(items) {
  if (!items.length) {
    return null;
  }

  const totalDry = sumNumericField(items, 'dryQuantity');
  const totalWet = sumNumericField(items, 'wetQuantity');
  const totalQuantity = sumNumericField(items, 'quantity');
  const totalKgNet = sumNumericField(items, 'quantityKg');
  const totalSubtotal = sumNumericField(items, 'subtotal');
  const stockUnit = items[0].quantityUnit || 'kg';
  const hasMixedUnits = hasMixedQuantityUnits(items);
  const weightedUnitCost = totalKgNet > 0 ? totalSubtotal / totalKgNet : 0;
  const weightedMoisture = calculateWeightedMoisture(items, totalKgNet);

  return {
    warehouseLabel: formatWarehouseLocation(items[0]),
    stockUnit,
    quantityBasisLabel: hasMixedUnits ? 'KG net basis' : `${stockUnit === 'kg' ? 'kg' : 'sacks'} stock`,
    supplierLabel: getMergedSupplierLabel(items),
    moistureLabel:
      weightedMoisture === null ? 'Mixed' : `${formatNumber(weightedMoisture)}%`,
    dryLabel: hasMixedUnits ? 'Mixed units' : formatQuantity(totalDry, stockUnit),
    wetLabel: hasMixedUnits ? 'Mixed units' : formatQuantity(totalWet, stockUnit),
    totalQuantityLabel: hasMixedUnits ? 'Mixed units' : formatQuantity(totalQuantity, stockUnit),
    totalDry,
    totalWet,
    totalQuantity,
    totalKgNet,
    totalSubtotal,
    weightedUnitCost,
  };
}

function hasMixedQuantityUnits(items) {
  const quantityUnits = new Set(
    items.map((item) => normalizeMergeValue(item.quantityUnit || 'kg'))
  );

  return quantityUnits.size > 1;
}

function getMergedSupplierLabel(items) {
  const supplierLabels = new Set(
    items.map((item) =>
      normalizeMergeValue(formatSupplierDisplay(item.sacksMark, item.companyName))
    )
  );

  if (supplierLabels.size === 1) {
    return formatSupplierDisplay(items[0].sacksMark, items[0].companyName);
  }

  return 'Mixed Suppliers';
}

function calculateWeightedMoisture(items, totalKgNet) {
  if (totalKgNet <= 0) {
    return null;
  }

  const weightedTotal = items.reduce((total, item) => {
    const moistureValue = parseMoistureContent(item.moistureContent);

    if (moistureValue === null) {
      return null;
    }

    if (total === null) {
      return null;
    }

    return total + Number(item.quantityKg || 0) * moistureValue;
  }, 0);

  return weightedTotal === null ? null : weightedTotal / totalKgNet;
}

function parseMoistureContent(value) {
  const normalizedValue = String(value || '').trim();

  if (!normalizedValue) {
    return null;
  }

  const numberValue = Number(normalizedValue.replace('%', ''));

  return Number.isFinite(numberValue) ? numberValue : null;
}

function formatMoistureContent(value) {
  const moistureValue = parseMoistureContent(value);

  if (moistureValue === null) {
    return String(value || '').trim() || '-';
  }

  return `${formatNumber(moistureValue)}%`;
}

function sumNumericField(items, fieldName) {
  return items.reduce((total, item) => total + Number(item[fieldName] || 0), 0);
}

function normalizeMergeValue(value) {
  return String(value || '').trim().toLowerCase();
}

function formatPurchaseOrderCode(item) {
  if (item.poCode) {
    return item.poCode;
  }

  return item.poCode || `PO-${String(item.purchaseOrderId || '').padStart(4, '0')}`;
}

function getMergedSourcePoCodes(item) {
  if (!item.isMergedInventory || !Array.isArray(item.sourcePurchaseOrders)) {
    return [];
  }

  return Array.from(
    new Set(
      item.sourcePurchaseOrders
        .map((source) => String(source.poCode || '').trim())
        .filter(Boolean)
    )
  );
}

function getMergedSourcePoCodeDisplay(item, limit = 2) {
  const poCodes = getMergedSourcePoCodes(item);

  if (!poCodes.length) {
    return {
      label: '',
      title: '',
    };
  }

  const visiblePoCodes = poCodes.slice(0, limit);
  const hiddenCount = poCodes.length - visiblePoCodes.length;

  return {
    label:
      hiddenCount > 0
        ? `${visiblePoCodes.join(', ')} +${hiddenCount} more`
        : visiblePoCodes.join(', '),
    title: poCodes.join(', '),
  };
}

function formatSupplierDisplay(sacksMark, supplierName) {
  const normalizedSacksMark = String(sacksMark || '').trim();
  const normalizedSupplierName = String(supplierName || '').trim();

  if (normalizedSacksMark && normalizedSupplierName) {
    return `${normalizedSacksMark} - ${normalizedSupplierName}`;
  }

  return normalizedSacksMark || normalizedSupplierName || '-';
}

function formatSacksMarkDisplay(item) {
  return String(item.sacksMark || '').trim() || '-';
}

function formatSourceSacksMark(source) {
  const directSacksMark = String(source?.sacksMark || '').trim();

  if (directSacksMark) {
    return directSacksMark;
  }

  const supplierLabel = String(source?.supplierLabel || '').trim();
  const [sacksMark] = supplierLabel.split(' - ');
  return String(sacksMark || '').trim() || '-';
}

function formatWarehouseLocation(item) {
  return String(item.warehouseLocation || '').trim() || '-';
}

function formatSeaweedType(item) {
  return String(item.seaweedType || '').trim() || '-';
}

function formatQuantity(value, unit = 'kg') {
  return `${formatNumber(value)} ${unit === 'kg' ? 'kg' : 'sacks'}`;
}

function formatInventoryQuantity(item, fieldName) {
  const labelByFieldName = {
    dryQuantity: item.dryQuantityLabel,
    wetQuantity: item.wetQuantityLabel,
    quantity: item.quantityLabel,
  };
  const label = String(labelByFieldName[fieldName] || '').trim();

  if (label) {
    return label;
  }

  return formatQuantity(item[fieldName], item.quantityUnit);
}

function formatReleasedInventoryQuantity(item) {
  return formatQuantity(getReleasedInventoryQuantity(item), getInventoryQuantityUnit(item));
}

function formatAvailableInventoryQuantity(item) {
  return formatQuantity(getAvailableInventoryQuantity(item), getInventoryQuantityUnit(item));
}

function formatKgNet(value) {
  return `${formatNumber(value)} kg`;
}

function formatUnitCost(value) {
  return `${formatPesoUnitCost(value)}/kg`;
}

function formatInventorySubtotal(value) {
  return `₱${new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0))}`;
}

function formatNumber(value) {
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

function formatPesoUnitCost(value) {
  return new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

function toDateInputValue(value) {
  if (!value) {
    return '';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

function splitReceivingNote(value) {
  const note = String(value || '').trim();
  const receiptMarker = findReceiptMarker(note);

  if (receiptMarker === -1) {
    return {
      remarks: note,
      receiptDetails: '',
    };
  }

  return {
    remarks: note.slice(0, receiptMarker).trim(),
    receiptDetails: formatReceiptDetails(note.slice(receiptMarker).trim()),
  };
}

function parseReceiptDetails(value) {
  return String(value || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const separatorIndex = line.indexOf(':');

      if (separatorIndex === -1) {
        return null;
      }

      return {
        label: line.slice(0, separatorIndex).trim(),
        value: line.slice(separatorIndex + 1).trim(),
      };
    })
    .filter(Boolean);
}

function findReceiptMarker(value) {
  const match = value.match(
    /(P\.O\. Code:|Ticket No\.:|Customer:|Commodity:|Plate #:|Driver:|Weigher:|Inbound Wt kg:|In Bound Wt kg:|Inbound Date:|In Bound Date:|Outbound Wt kg:|Outbound:|Outbound Date:|Gross KG:|Tare KG:|Net KG:)/
  );

  return match ? match.index : -1;
}

function formatReceiptDetails(value) {
  return value.replace(
    /\s+(P\.O\. Code:|Ticket No\.:|Customer:|Commodity:|Plate #:|Driver:|Weigher:|Inbound Wt kg:|In Bound Wt kg:|Inbound Date:|In Bound Date:|Outbound Wt kg:|Outbound:|Outbound Date:|Gross KG:|Tare KG:|Net KG:)/g,
    '\n$1'
  );
}

export default InventoryPage;
